import React from 'react';
import 'react-dates-21/lib/css/_datepicker.css';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-dates-21'. 'node_modules/react-dates-21/index.js' implicitly has an 'any' type.
import { DayPickerSingleDateController } from 'react-dates-21';
import type { Moment } from 'moment';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { colors } from '@grnhse/seedling/lib/azalea/constants';
import { BackArrow, ForwardArrow } from '@grnhse/seedling/lib/azalea/icons';
import {
  BodyText,
  BoldBody,
  BodyHeader,
} from '@grnhse/seedling/lib/azalea/typography';
import type { SlotsByDay } from '../../types';
import { useBonsai } from '@grnhse/bonsai/lib/hooks';
import type { BonsaiConfiguration } from '@grnhse/bonsai/lib/core';
import { Body } from '@grnhse/bonsai/lib/components/typography';

type Props = {
  start: string;
  end: string;
  currentMonth: Moment;
  selectedDate: Moment | null | undefined;
  isMobile?: boolean;
  availableSlotsByDay: SlotsByDay;
  selectDate: (arg1: Moment) => void;
  setCurrentMonth: (arg1: Moment) => void;
  bonsaiEnabled: boolean;
};

const isToday = (day: any) => {
  const today = moment().format('MMMM DD YYYY');
  const formattedDay = day.format('MMMM DD YYYY');

  return formattedDay === today;
};

export default function DatePicker(props: Props) {
  const {
    start,
    end,
    selectedDate,
    availableSlotsByDay,
    currentMonth,
    selectDate,
    setCurrentMonth,
    isMobile,
    bonsaiEnabled,
  } = props;
  const startDate = moment(start);
  const endDate = moment(end);

  const bonsaiConfiguration = useBonsai();

  const dayColor = bonsaiEnabled
    ? bonsaiConfiguration.buttonColor
    : colors.mediumGreenhouseGreen;

  const selectedColor = bonsaiEnabled
    ? bonsaiConfiguration.buttonColor
    : colors.evergreen;

  const selectedTextColor = bonsaiEnabled
    ? bonsaiConfiguration.buttonTextColor
    : colors.white;

  moment.updateLocale('en', { weekdaysMin: 'S_M_T_W_TH_F_S'.split('_') });

  return (
    <Wrapper
      isMobile={isMobile}
      bonsaiConfiguration={bonsaiConfiguration}
      bonsaiEnabled={bonsaiEnabled}
      dayColor={dayColor}
      selectedColor={selectedColor}
      selectedTextColor={selectedTextColor}
    >
      <DayPickerSingleDateController
        date={selectedDate}
        transitionDuration={0}
        numberOfMonths={1}
        daySize={50}
        noBorder={true}
        hideKeyboardShortcutsPanel={true}
        // @ts-expect-error - TS7006 - Parameter 'date' implicitly has an 'any' type.
        onDateChange={(date) => {
          selectDate(date);
        }}
        // @ts-expect-error - TS7006 - Parameter 'day' implicitly has an 'any' type.
        isDayHighlighted={(day) => {
          return availableSlotsByDay[day.format('YYYY-MM-DD')]?.length > 0;
        }}
        isDayBlocked={isToday}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderDayContents={(day: any) => (
          <DayDisplay bonsaiEnabled={bonsaiEnabled} day={day} />
        )}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderMonthElement={({ month }: { month: any }) => (
          <MonthDisplay bonsaiEnabled={bonsaiEnabled} month={month} />
        )}
        // @ts-expect-error - TS7006 - Parameter 'day' implicitly has an 'any' type.
        renderWeekHeaderElement={(day) =>
          bonsaiEnabled ? (
            <div style={{ marginTop: '8px' }}>
              <Body medium>{day.toUpperCase()}</Body>
            </div>
          ) : (
            <BodyHeader>{day.toUpperCase()}</BodyHeader>
          )
        }
        // @ts-expect-error - TS7006 - Parameter 'day' implicitly has an 'any' type.
        isOutsideRange={(day) => {
          return (
            day.isAfter(endDate.endOf('day')) ||
            day.isBefore(startDate.startOf('day')) ||
            !availableSlotsByDay[day.format('YYYY-MM-DD')] ||
            availableSlotsByDay[day.format('YYYY-MM-DD')]?.length === 0
          );
        }}
        // @ts-expect-error - TS7006 - Parameter 'day' implicitly has an 'any' type.
        onNextMonthClick={(day) => {
          setCurrentMonth(day.startOf('month'));
        }}
        // @ts-expect-error - TS7006 - Parameter 'day' implicitly has an 'any' type.
        onPrevMonthClick={(day) => {
          setCurrentMonth(day.startOf('month'));
        }}
        navPrev={
          <NavPrev disabled={currentMonth.month() === startDate.month()} />
        }
        navNext={
          <NavNext disabled={currentMonth.month() === endDate.month()} />
        }
      />
    </Wrapper>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DayDisplay = (props: { bonsaiEnabled: boolean; day: any }) => {
  if (isToday(props.day)) {
    return props.bonsaiEnabled ? (
      <Body medium>
        <span>{props.day.date()}</span>
      </Body>
    ) : (
      <Today>
        <span>{props.day.date()}</span>
      </Today>
    );
  }

  return (
    <div className="day">
      <span>{props.day.date()}</span>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MonthDisplay = (props: { month: any; bonsaiEnabled: boolean }) => {
  if (props.bonsaiEnabled) {
    return (
      <div style={{ marginLeft: 15 }}>
        <Body medium>{props.month.format('MMMM YYYY')}</Body>
      </div>
    );
  }

  return (
    <BodyText style={{ marginTop: 0, marginLeft: 15 }}>
      <BoldBody>{props.month.format('MMMM YYYY')}</BoldBody>
    </BodyText>
  );
};

const NavPrev = (props: { disabled: boolean }) => (
  <div
    style={{
      right: '62px',
      padding: '3px',
      position: 'absolute',
      top: '20px',
      pointerEvents: props.disabled ? 'none' : 'auto',
    }}
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'.
    tabIndex="0"
  >
    <BackArrow
      width="20"
      color={props.disabled ? colors.mediumGrey : colors.evergreen}
    />
  </div>
);

const NavNext = (props: { disabled: boolean }) => (
  <div
    style={{
      padding: '3px',
      position: 'absolute',
      right: '30px',
      top: '20px',
      pointerEvents: props.disabled ? 'none' : 'auto',
    }}
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'.
    tabIndex="0"
  >
    <ForwardArrow
      width="20"
      color={props.disabled ? colors.mediumGrey : colors.evergreen}
    />
  </div>
);

const Wrapper = styled.div<{
  dayColor: string;
  selectedColor: string;
  bonsaiConfiguration: BonsaiConfiguration;
  bonsaiEnabled: boolean;
  isMobile?: boolean;
  selectedTextColor: string;
}>`
  height: 390px;

  ${({ bonsaiConfiguration, bonsaiEnabled }) =>
    bonsaiEnabled && `font-family: ${bonsaiConfiguration.secondaryFont};`}

  .CalendarMonth {
    ${({ isMobile }) => isMobile && 'padding-left: 0 !important;'}

    &_caption {
      text-align: start;
    }
  }

  .DayPicker {
    &_weekHeader {
      top: 50px;

      ${({ isMobile }) => isMobile && 'padding-left: 0 !important;'}
    }
  }

  .CalendarDay {
    &__default {
      border: 0 !important;
      border-radius: 100%;
    }

    &__blocked_out_of_range {
      color: ${colors.mediumGrey};

      &:hover {
        color: ${colors.mediumGrey};
      }
    }

    &__blocked_calendar {
      &:after {
        display: none;
      }
    }

    &__highlighted_calendar {
      background: none;
      color: ${({ dayColor }) => dayColor};
      position: relative;

      &:after {
        content: ' ';
        position: absolute;
        top: 50%;
        right: 50%;
        transform: translateY(10px) translateX(2px);
        background: ${({ dayColor }) => dayColor};
        width: 4px;
        height: 4px;
        border-radius: 100%;
      }

      &:hover {
        background: none;
      }

      .day {
        border-radius: 100%;

        width: 80%;
        height: 80%;
        margin: 2px auto 0;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: ${colors.lighterGrey};
          color: ${({ dayColor }) => dayColor};
        }
      }
    }

    &__selected {
      color: ${({ selectedTextColor }) => selectedTextColor};

      &:after {
        background: ${({ selectedTextColor }) => selectedTextColor};
      }

      .day {
        color: ${({ selectedTextColor }) => selectedTextColor};
        background: ${({ selectedColor }) => selectedColor};

        &:hover {
          color: ${({ selectedTextColor }) => selectedTextColor};
          background: ${({ selectedColor }) => selectedColor};

          &:after {
            background: ${({ selectedTextColor }) => selectedTextColor};
          }
        }
      }
    }
  }
`;

const Today = styled(BodyHeader)`
  margin: 0;
`;
